import { createAdminAxios } from '../../utils/axiosConfig';
import { useNotification } from '../../contexts/NotificationContext';
import config from '../../contexts/ConfigContext';
import { newDlpProfileName } from '../../utils/dlpIdentifiers';

// Tables (DLP Sender config on Databricks), as exposed by the DLP Config API:
//  - dlp_projects        one row per project.
//  - dlp_quota_profiles  rows of (profile_name, project_id): a profile belongs to one project.
//  - dlp_scopes          one row per brand (brand_id NULL = all brands). The API groups the rows
//                        sharing (project_id, name) into one named scope with a brand list, which
//                        the page keeps as scopes + scopeBrands.

export interface DlpLabelModel {
    id: string;
    name: string;
    parent_id: string | null;
}

export interface DlpProjectModel {
    id: string;
    dlp_project_code: string;
    source_family: string;
    channels: string[];
    group_by: string[];
    label_field: string;
    ranking_metric: string;
    window_days: number | null;
    exclude_owned_media: boolean;
    exclude_already_sent: boolean;
    empty_label_fallback: string;
    is_active: boolean;
    dlp_labels: DlpLabelModel[];
}

export interface DlpScopeModel {
    scope_id: number;
    project_id: string;
    name: string;
    all_brands: boolean;
    channels: string[] | null;
    countries: string[] | null;
    industries: string[] | null;
    quota_profile: string;
    start_from: string;
    ended_at: string | null;
}

export interface DlpScopeBrandModel {
    scope_id: number;
    brand_id: string;
}

export interface DlpQuotaProfileRowModel {
    profile_name: string;
    project_id: string;
    channel: string | null;
    label: string | null;
    max_docs: number | null;
}

export interface DlpConfigurationModel {
    projects: DlpProjectModel[];
    scopes: DlpScopeModel[];
    scopeBrands: DlpScopeBrandModel[];
    profiles: DlpQuotaProfileRowModel[];
}

export interface DlpLookupModel {
    id: string;
    name: string;
}

// --- Shapes returned by the DLP Config API (see DMR-DataLabeling-DlpConfigToolApi/app/schemas.py) ---

interface ApiProject extends Omit<DlpProjectModel, 'label_field' | 'empty_label_fallback' | 'channels' | 'group_by' | 'dlp_labels'> {
    label_field: string | null;
    empty_label_fallback: string | null;
    channels: string[] | null;
    group_by: string[] | null;
    // Older API responses did not include parent_id; normalize roots on receipt.
    dlp_labels: (Omit<DlpLabelModel, 'parent_id'> & { parent_id?: string | null })[] | null;
}

interface ApiScope extends Omit<DlpScopeModel, 'quota_profile'> {
    quota_profile: string | null;
    brand_ids: string[];
}

interface ApiProfileRow {
    channel: string | null;
    label: string | null;
    max_docs: number | null;
}

interface ApiProfile {
    name: string;
    project_id: string;
    rows: ApiProfileRow[];
}

const toProject = (p: ApiProject): DlpProjectModel => ({
    ...p,
    label_field: p.label_field ?? '',
    empty_label_fallback: p.empty_label_fallback ?? '',
    channels: p.channels ?? [],
    group_by: p.group_by ?? [],
    dlp_labels: (p.dlp_labels ?? []).map(label => ({ ...label, parent_id: label.parent_id || null }))
});

const toScope = ({ brand_ids, ...scope }: ApiScope): DlpScopeModel => ({ ...scope, quota_profile: scope.quota_profile ?? '' });

const toApiScope = ({ scope_id, project_id, ...fields }: DlpScopeModel, brandIds: string[]) => ({ ...fields, brand_ids: brandIds });

const toProfileRows = (profile: ApiProfile): DlpQuotaProfileRowModel[] =>
    profile.rows.map(r => ({ ...r, profile_name: profile.name, project_id: profile.project_id }));

const toApiRows = (rows: DlpQuotaProfileRowModel[]): ApiProfileRow[] =>
    rows.map(({ channel, label, max_docs }) => ({ channel, label, max_docs }));

const segment = (value: string | number) => encodeURIComponent(String(value));

export class DlpProjectSelectionError extends Error {
    constructor(public readonly reason: 'missingProjectCode' | 'duplicateProjectCode') {
        super(reason);
        this.name = 'DlpProjectSelectionError';
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

// Every call is bound to the selected DLP .NET project. Python is never browser-facing.
class DlpConfigurationService {
    readonly baseUrl: string;
    readonly axios;
    notification = useNotification();

    constructor(projectId: number) {
        this.baseUrl = `${config.API_URL}/api/DlpConfiguration/projects/${segment(projectId)}`;
        this.axios = createAdminAxios(projectId);
    }

    async CheckAccess(): Promise<void> {
        await this.axios.get(`${this.baseUrl}/access`);
    }

    private errorMessage(error: any): string {
        if (error?.response?.status === 401) return 'Your session is invalid or expired. Please sign in again.';
        const data = error?.response?.data;
        if (Array.isArray(data?.detail)) {
            return data.detail.map((issue: any) => `${issue.loc?.join('.') ?? 'request'}: ${issue.msg}`).join('; ');
        }
        return typeof data?.detail === 'string' ? data.detail
            : typeof data?.detail?.message === 'string' ? data.detail.message
                : error?.response ? 'The configuration service could not complete the request.'
                    : error?.message ?? 'Unexpected error';
    }

    private fail(message: string, error: any) {
        this.notification.showNotification(`${message}: ${this.errorMessage(error)}`, 'error');
        // Axios errors contain request headers: never log the error object/token.
        console.error(message, error?.response?.status);
    }

    // .NET derives the code/owner and resolves exactly one Databricks row.
    async GetConfiguration(): Promise<DlpConfigurationModel> {
        try {
            const response = await this.axios.get<ApiProject[]>(`${this.baseUrl}/projects`);
            const projects = response.data.filter(project => project.is_active === true).map(toProject);
            if (projects.length > 1) throw new DlpProjectSelectionError('duplicateProjectCode');
            const [project] = projects;
            if (!project) return { projects: [], scopes: [], scopeBrands: [], profiles: [] };

            const [scopes, profiles] = await Promise.all([
                this.axios.get<ApiScope[]>(`${this.baseUrl}/projects/${segment(project.id)}/scopes`),
                this.axios.get<ApiProfile[]>(`${this.baseUrl}/projects/${segment(project.id)}/profiles`)
            ]);
            const selectedScopes = scopes.data.filter(scope => scope.project_id === project.id);
            return {
                projects,
                scopes: selectedScopes.map(toScope),
                scopeBrands: selectedScopes.flatMap(s => s.brand_ids.map(brand_id => ({ scope_id: s.scope_id, brand_id }))),
                profiles: profiles.data.filter(profile => profile.project_id === project.id).flatMap(toProfileRows)
            };
        } catch (error) {
            if (error instanceof DlpProjectSelectionError) throw error;
            if ((error as any)?.response?.status === 409 &&
                (error as any)?.response?.data?.detail?.code === 'duplicate_project_code') {
                throw new DlpProjectSelectionError('duplicateProjectCode');
            }
            this.fail('Error loading DLP configuration', error);
            throw error;
        }
    }

    async GetBrands(): Promise<DlpLookupModel[]> {
        try {
            return (await this.axios.get<DlpLookupModel[]>(`${this.baseUrl}/brands`)).data;
        } catch (error) {
            this.fail('Error loading brands', error);
            throw error;
        }
    }

    async GetCountries(): Promise<DlpLookupModel[]> {
        try {
            return (await this.axios.get<DlpLookupModel[]>(`${this.baseUrl}/countries`)).data;
        } catch (error) {
            this.fail('Error loading countries', error);
            throw error;
        }
    }

    async UpdateProject(project: DlpProjectModel): Promise<boolean> {
        try {
            // Mapping is server-owned; the label vocabulary is read-only on this page.
            const { id, dlp_project_code, dlp_labels, ...fields } = project;
            await this.axios.put(`${this.baseUrl}/projects/${segment(id)}`, fields);
            this.notification.showNotification('Project saved', 'info');
            return true;
        } catch (error) {
            this.fail('Error saving project', error);
            return false;
        }
    }

    // Creates the scope with its brands (one dlp_scopes row per brand)
    async CreateScope(scope: DlpScopeModel, brandIds: string[]): Promise<DlpScopeModel | null> {
        try {
            const response = await this.axios.post<ApiScope>(`${this.baseUrl}/projects/${segment(scope.project_id)}/scopes`, toApiScope(scope, brandIds));
            this.notification.showNotification('Scope saved', 'info');
            return toScope(response.data);
        } catch (error) {
            this.fail('Error saving scope', error);
            return null;
        }
    }

    // Saves the scope found by its current name (scope.name may rename it) with its full brand list
    async UpdateScope(currentName: string, scope: DlpScopeModel, brandIds: string[]): Promise<DlpScopeModel | null> {
        try {
            const response = await this.axios.put<ApiScope>(
                `${this.baseUrl}/projects/${segment(scope.project_id)}/scopes`,
                toApiScope(scope, brandIds), { params: { name: currentName } }
            );
            this.notification.showNotification('Scope saved', 'info');
            return toScope(response.data);
        } catch (error) {
            this.fail('Error saving scope', error);
            return null;
        }
    }

    async DeleteScope(projectId: string, name: string): Promise<boolean> {
        try {
            await this.axios.delete(`${this.baseUrl}/projects/${segment(projectId)}/scopes`, { params: { name } });
            this.notification.showNotification('Scope deleted', 'info');
            return true;
        } catch (error) {
            this.fail('Error deleting scope', error);
            return false;
        }
    }

    // A profile exists only through its rows: a new one starts from the given rows
    // (a copy of another profile) or from a single "any channel / any label" row.
    async CreateQuotaProfile(projectId: string, copyFrom?: { name: string, rows: DlpQuotaProfileRowModel[] }): Promise<string | null> {
        try {
            const name = newDlpProfileName(copyFrom ? `${copyFrom.name}_copy` : 'new_profile');
            const rows: ApiProfileRow[] = copyFrom ? toApiRows(copyFrom.rows) : [{ channel: null, label: null, max_docs: null }];
            await this.axios.put(`${this.baseUrl}/projects/${segment(projectId)}/profiles`, { name, rows }, { params: { name } });
            return name;
        } catch (error) {
            this.fail('Error creating quota profile', error);
            return null;
        }
    }

    // Replaces all rows; a rename cascades to the scopes pointing at the profile (done by the API)
    async UpdateQuotaProfile(projectId: string, currentName: string, newName: string, rows: DlpQuotaProfileRowModel[]): Promise<boolean> {
        try {
            await this.axios.put(`${this.baseUrl}/projects/${segment(projectId)}/profiles`, {
                name: newName,
                rows: toApiRows(rows)
            }, { params: { name: currentName } });
            this.notification.showNotification('Quota profile saved', 'info');
            return true;
        } catch (error) {
            this.fail('Error saving quota profile', error);
            return false;
        }
    }

    async DeleteQuotaProfile(projectId: string, profileName: string): Promise<boolean> {
        try {
            await this.axios.delete(`${this.baseUrl}/projects/${segment(projectId)}/profiles`, { params: { name: profileName } });
            this.notification.showNotification('Quota profile deleted', 'info');
            return true;
        } catch (error) {
            this.fail('Error deleting quota profile', error);
            return false;
        }
    }
}

export default DlpConfigurationService;
